import './assets/css/index'

import React, { lazy } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import PrivateLayout from './components/private-layout'
import { routesDef } from './config/routes'
import { CenterScreen } from '@ztechapps/webui.util'
import { BlogPreviewChunkFailed } from '@ztechapps/blog.webui'
import { DbRestoreProvider, LoadingLayout, PageLayoutNotFound } from '@ztechapps/webui.page'
import { ApolloProvider } from '@apollo/client'
import { AppContextProvider } from './hooks/useAppContext'
import { setupApolloClient } from './config/graphql'
import { appSettings } from './config/app-settings'
import SaleItemPaymentSuccess from './view/sales/item/payment-status/Success'
import SaleItemPaymentFailed from './view/sales/item/payment-status/Failed'

//home pages
const Dashboard = lazy(() => import('./view/overview'))

//product pages
const ProductList = lazy(() => import('./view/products/list'))
const ProductItem = lazy(() => import('./view/products/item'))

//contacts pages
const ContactList = lazy(() => import('./view/contacts/list'))
const CustomerItem = lazy(() => import('./view/contacts/item/user/customer-item'))
const VendorItem = lazy(() => import('./view/contacts/item/user/vendor-item'))
const CompanyItem = lazy(() => import('./view/contacts/item/company'))

const ConversationInbox = lazy(() => import('./view/conversation/list'))
// The inbox detail renders in the list's off-canvas outlet; the module only holds a named export,
// so map it to a default for the lazy boundary.
const ConversationDetailPage = lazy(() =>
  import('./view/conversation/list/ConversationDetail').then((module) => ({ default: module.ConversationDetail }))
)
const ConversationItem = lazy(() => import('./view/conversation/item'))
//adjustment
const AdjustmentList = lazy(() => import('./view/adjustments/list'))
const AdjustmentItem = lazy(() => import('./view/adjustments/item'))

//sales
const SaleList = React.lazy(() => import('./view/sales/list'))
const SaleItem = React.lazy(() => import('./view/sales/item'))

//manufacturing pages
const ProductionList = lazy(() => import('./view/productions/list'))
const ProductionItem = lazy(() => import('./view/productions/item'))
const ProjectList = lazy(() => import('./view/projects/list'))
const ProjectItem = lazy(() => import('./view/projects/item'))
const ReturnListItem = lazy(() => import('./view/returns/list-items'))
const ReturnItem = lazy(() => import('./view/returns/item'))
const TaskItemPage = lazy(() => import('./view/tasks/item'))

//purchase-order
const PurchaseList = lazy(() => import('./view/purchases/list'))
const PurchaseItem = lazy(() => import('./view/purchases/item'))

//logistics

const LogisticsPage = lazy(() => import('./view/logistics/list'))
const LogisticsItem = lazy(() => import('./view/logistics/item'))

//settings pages

const Settings = lazy(() => import('./view/settings'))
const BlogsPage = lazy(() => import('./view/settings/blogs/BlogsPage').then((m) => ({ default: m.BlogsPage })))
const BlogItemPage = lazy(() => import('./view/settings/blogs/BlogItemPage').then((m) => ({ default: m.BlogItemPage })))

// Standalone preview — deliberately outside PrivateLayout (no admin nav, no access
// gate). A rejected chunk import renders a terminal state instead of hanging on
// the Suspense fallback forever.
const BlogPreviewPage = lazy(() => import('./view/blog-preview').catch(() => ({ default: BlogPreviewChunkFailed })))

// Dev-only prototype runner, outside PrivateLayout like the preview above. `import.meta.env.DEV` is
// false in a production build, so the route and its chunk are dropped from the bundle.
const PrototypeRoutes = import.meta.env.DEV ? lazy(() => import('./prototype')) : null

//reports

const Reports = lazy(() => import('./view/reports'))
const ClosedOrderReport = lazy(() => import('./view/reports/closedOrder'))
const ArAgingReportPage = lazy(() => import('./view/reports/arAging'))
const ArAgingCompanyDetailPage = lazy(() => import('./view/reports/arAging/ArAgingCompanyDetailPage'))
const OpenOrderReport = lazy(() => import('./view/reports/openOrder'))
const OrderReport = lazy(() => import('./view/reports/order'))
const OrderWithProductsReport = lazy(() => import('./view/reports/orderWithProducts'))
const ProductReport = lazy(() => import('./view/reports/product'))
const CustomerReport = lazy(() => import('./view/reports/customer'))
const InventoryReport = lazy(() => import('./view/reports/inventory'))
const InventoryBySerialNumberReport = lazy(() => import('./view/reports/inventoryBySerialNumber'))
const TaskListPage = lazy(() => import('./view/tasks/list'))
const DriverPickupSummaryReport = lazy(() => import('./components/driver-pickup/ReportDriverPickupSummaryPage'))

//account
const Login = lazy(() => import('./view/account/login'))
const LoginSuccess = lazy(() => import('./view/account/login-success'))
const JoinPage = lazy(() => import('./view/account/join'))
const LoginFailed = lazy(() => import('./view/account/login-failed'))
const ChangePassword = lazy(() => import('./view/account/change-password'))
const LoadingPage = lazy(() => import('./view/account/loading/LoadingPage'))

//utilities
const UtilitiesPage = lazy(() => import('./view/utilities'))
const QbCompareProductsPage = lazy(() => import('./view/utilities/qb-compare/products'))

//notifications
const Notifications = lazy(() => import('./view/notifications'))

export const App = () => {
  const [apolloClient, setApolloClient] = React.useState(null)

  React.useEffect(() => {
    const iife = async () => {
      const localApolloClient = setupApolloClient()

      setApolloClient(localApolloClient)
    }
    iife()
  }, [])

  if (!apolloClient) return <CenterScreen>Loading....</CenterScreen>

  return (
    <BrowserRouter>
      <ApolloProvider client={apolloClient}>
        <AppContextProvider>
          <DbRestoreProvider environment={appSettings.nodeEnv} loginPath={routesDef.LoginPage.path}>
            <LoadingLayout>
              <Routes>
                <Route index element={<LoadingPage />} />
                <Route path={routesDef.AuthSuccessPage.path} element={<LoginSuccess />} />
                <Route path={routesDef.AuthFailedPage.path} element={<LoginFailed />} />
                <Route path={routesDef.JoinPage.path} element={<JoinPage />} />
                <Route path={routesDef.LoginPage.path} element={<Login />} />
                <Route path={routesDef.ChangePasswordPage.path} element={<ChangePassword />} />

                {/* Standalone preview — outside the /app tree, mounts no admin layout or access gate. */}
                <Route path={routesDef.BlogPreviewPage.path} element={<BlogPreviewPage />} />

                {/* Dev-only prototype runner — `null` in a production build, which <Routes> skips. */}
                {PrototypeRoutes && <Route path="/prototype/*" element={<PrototypeRoutes />} />}

                <Route path={'/app'} element={<PrivateLayout />}>
                  <Route index path={routesDef.OverviewPage.path} element={<Dashboard />} />
                  <Route path={routesDef.OrdersPage.path} element={<SaleList />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <SaleItem />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.OrderItemPage.path} element={<SaleItem />} />
                  <Route path={routesDef.OrderItemPaymentFailedPage.path} element={<SaleItemPaymentFailed />} />
                  <Route path={routesDef.OrderItemPaymentSuccessPage.path} element={<SaleItemPaymentSuccess />} />

                  {/*production/workoder pages*/}
                  <Route path={routesDef.ProductionPage.path} element={<ProductionList />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <ProductionItem />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.ProductionItemPage.path} element={<ProductionItem />} />

                  {/*Adjustment Routes*/}
                  <Route path={routesDef.AdjustmentPage.path} element={<AdjustmentList />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <AdjustmentItem />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.AdjustmentItemPage.path} element={<AdjustmentItem />} />

                  {/*Products Routes*/}
                  <Route path={routesDef.ProductListPage.path} element={<ProductList />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <ProductItem />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.ProductItemPage.path} element={<ProductItem />} />

                  {/*Customer Routes*/}
                  <Route path={routesDef.ContactsPage.path} element={<ContactList />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <CompanyItem />
                        </LoadingLayout>
                      }
                    />

                    <Route
                      path="company/:_id"
                      element={
                        <LoadingLayout>
                          <CompanyItem />
                        </LoadingLayout>
                      }
                    />
                    <Route
                      path="customer/:_id"
                      element={
                        <LoadingLayout>
                          <CustomerItem />
                        </LoadingLayout>
                      }
                    />
                    <Route
                      path="vendor/:_id"
                      element={
                        <LoadingLayout>
                          <VendorItem />
                        </LoadingLayout>
                      }
                    />
                  </Route>

                  <Route path={routesDef.CustomerItemPage.path} element={<CustomerItem />} />
                  <Route path={routesDef.CompanyItemPage.path} element={<CompanyItem />} />
                  <Route path={routesDef.VendorItemPage.path} element={<VendorItem />} />

                  {/*Project Routes*/}
                  <Route path={routesDef.ProjectsPage.path} element={<ProjectList />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <ProjectItem />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.ProjectItemPage.path} element={<ProjectItem />} />

                  <Route path={routesDef.TaskItemPage.path} element={<TaskItemPage />} />
                  <Route path={routesDef.TaskCalendarPage.path} element={<TaskListPage />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <TaskItemPage />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.TaskListPage.path} element={<TaskListPage />} />
                  <Route path={routesDef.TaskBoardPage.path} element={<TaskListPage />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <TaskItemPage />
                        </LoadingLayout>
                      }
                    />
                  </Route>

                  <Route path={routesDef.ConversationPage.path} element={<ConversationInbox />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <ConversationDetailPage />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.ConversationItemPage.path} element={<ConversationItem />} />

                  {/*Logistics routes*/}
                  <Route path={routesDef.LogisticListPage.path} element={<LogisticsPage />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <LogisticsItem />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.LogisticItemPage.path} element={<LogisticsItem />} />

                  <Route path={routesDef.BlogsPage.path} element={<BlogsPage />} />
                  <Route path={routesDef.BlogItemPage.path} element={<BlogItemPage />} />
                  <Route path={routesDef.ReportPage.path} element={<Reports />} />
                  <Route path={routesDef.SettingsDetailPage.path} element={<Settings />} />

                  <Route path={routesDef.ReturnListItemPage.path} element={<ReturnListItem />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <ReturnItem />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.ReturnDetailPage.path} element={<ReturnItem />} />
                  <Route path={routesDef.PurchasesPage.path} element={<PurchaseList />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <PurchaseItem />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.PurchaseItemPage.path} element={<PurchaseItem />} />

                  {/*  Reports*/}
                  <Route path={routesDef.ReportArAgingPage.path} element={<ArAgingReportPage />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <ArAgingCompanyDetailPage />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.ReportClosedOrderPage.path} element={<ClosedOrderReport />} />
                  <Route path={routesDef.ReportOpenOrderPage.path} element={<OpenOrderReport />} />
                  <Route path={routesDef.ReportOrderPage.path} element={<OrderReport />} />
                  <Route path={routesDef.ReportOrderWithProductsPage.path} element={<OrderWithProductsReport />} />
                  <Route path={routesDef.ReportProductPage.path} element={<ProductReport />} />
                  <Route path={routesDef.ReportCustomerPage.path} element={<CustomerReport />} />
                  <Route path={routesDef.ReportInventoryPage.path} element={<InventoryReport />} />
                  <Route
                    path={routesDef.ReportInventoryBySerialNumberPage.path}
                    element={<InventoryBySerialNumberReport />}
                  />
                  <Route path={routesDef.ReportDriverPickupSummaryPage.path} element={<DriverPickupSummaryReport />} />

                  {/*Utilities Pages*/}
                  <Route path={routesDef.UtilitiesPage.path} element={<UtilitiesPage />} />
                  <Route path={routesDef.QbCompareProductsPage.path} element={<QbCompareProductsPage />} />

                  {/*Notification Page*/}
                  <Route path={routesDef.NotificationListPage.path} element={<Notifications />} />
                </Route>

                <Route path="*" element={<PageLayoutNotFound />} />
              </Routes>
            </LoadingLayout>
          </DbRestoreProvider>
        </AppContextProvider>
      </ApolloProvider>
    </BrowserRouter>
  )
}
